/**
 * Class that generates readable log entries from effects.
 * Now refactored to return message keys and parameters for real-time i18n support.
 */
class LogEntryGenerator {
    constructor() {
        this.formatters = {
            // Player actions
            PLAYER_ACTION: this.formatPlayerAction.bind(this),
            
            // Card damage (intermediary steps are ignored)
            MODIFY_CARD_DURABILITY: () => null,        
            MODIFY_CARD_DURABILITY_RESERVE: () => null, 
            
            // Card movement
            MOVE_CARD: this.formatCardMove.bind(this),
            
            // Resource changes (reservations)
            MODIFY_CONSCIOUSNESS_RESERVE: this.formatConsciousnessChange.bind(this),
            MODIFY_SCALE_RESERVE: this.formatScaleChange.bind(this),
            MODIFY_CONSCIOUSNESS: this.formatConsciousnessChange.bind(this),
            MODIFY_SCALE: this.formatScaleChange.bind(this),
            SET_CONSCIOUSNESS: this.formatSetConsciousness.bind(this),
            SET_SCALE: this.formatSetScale.bind(this),
            
            // Actual results
            CONSCIOUSNESS_CHANGED: this.formatActualConsciousnessChange.bind(this),
            SCALE_CHANGED: this.formatActualScaleChange.bind(this),
            CARD_DURABILITY_CHANGED: this.formatActualCardDamage.bind(this),
            
            // Operations
            DRAW_CARD: this.formatCardDraw.bind(this),
            PROCESS_EXPOSE_CARD_BY_TYPE: this.formatExposeCard.bind(this),
            REMOVE_CARD_FROM_GAME: this.formatRemoveCard.bind(this),
            PROCESS_REDEVELOPMENT_PLAN: this.formatRedevelopmentPlan.bind(this),
            
            // Turn management
            END_TURN_OWNER: this.formatTurnEnd.bind(this),
            START_TURN_OWNER: this.formatTurnStart.bind(this),
            
            // Default formatter
            DEFAULT: this.formatDefault.bind(this)
        };
    }

    /**
     * Generates a log entry object.
     */
    generateEntry(effectType, args, sourceCard, gameState) {
        try {
            const formatter = this.formatters[effectType] || this.formatters.DEFAULT;
            const entry = formatter(args, sourceCard, gameState);
            
            if (entry) {
                entry.id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                entry.timestamp = Date.now();
                entry.type = 'effect';
                entry.effectType = effectType;

                entry.playerId = args.player_id || 
                                sourceCard?.owner || 
                                (effectType.includes('TURN_OWNER') ? args.player_id : null) ||
                                'system';

                // Structure sourceCard info for real-time translation and UI interaction
                if (sourceCard) {
                    entry.sourceCardInfo = {
                        cardId: sourceCard.id,
                        instanceId: sourceCard.instance_id,
                        ownerId: sourceCard.owner
                    };
                }
            }
            
            return entry;
        } catch (error) {
            console.error('[LogEntryGenerator] Error generating entry:', error);
            return null;
        }
    }

    formatPlayerAction(args, sourceCard, gameState) {
        const card = gameState.all_card_instances[args.card_id];
        if (!card) return null;

        return {
            messageKey: args.action_type === 'play_card' ? 'LOG.PLAY_CARD' : 'LOG.ACTIVATE_EFFECT',
            params: {
                cardId: card.id
            },
            details: {
                cardId: card.instance_id,
                actionType: args.action_type,
                cardType: card.card_type
            }
        };
    }

    formatCardMove(args, sourceCard, gameState) {
        const card = args.card_to_move || gameState.all_card_instances[args.card_id];
        if (!card || args.destination_pile === 'playing_event') return null;

        let messageKey = 'LOG.MOVE_ZONE';
        const params = {
            cardId: card.id,
            destZoneKey: `LOG.ZONES.${args.destination_pile}`
        };
        if (args.source_pile !== 'game_source') {
            params.sourceZoneKey = `LOG.ZONES.${args.source_pile}`;
        }

        // Visibility logic
        if (args.destination_pile !== 'field' && args.player_id === 'PLAYER2') {
            params.isHidden = true;
        }

        if (args.source_pile === 'game_source') {
            messageKey = 'LOG.ADD_CARD';
        } else if (args.source_pile === 'deck' && args.destination_pile === 'hand') {
            messageKey = 'LOG.DRAW_CARD';
        } else if (args.source_pile === 'hand' && args.destination_pile === 'field') {
            messageKey = 'LOG.PLACE_ON_FIELD';
        } else if (args.destination_pile === 'discard') {
            messageKey = 'LOG.MOVE_TO_DISCARD';
        }

        return {
            messageKey,
            params,
            details: {
                cardId: card.instance_id,
                sourceZone: args.source_pile,
                destZone: args.destination_pile
            }
        };
    }

    formatConsciousnessChange(args, sourceCard, gameState) {
        const amount = Math.abs(args.amount || 0);
        return {
            messageKey: (args.amount || 0) > 0 ? 'LOG.CONSCIOUSNESS_INCREASE' : 'LOG.CONSCIOUSNESS_DECREASE',
            params: { amount },
            details: { resource: 'consciousness', amount: args.amount }
        };
    }

    formatScaleChange(args, sourceCard, gameState) {
        const amount = Math.abs(args.amount || 0);
        return {
            messageKey: (args.amount || 0) > 0 ? 'LOG.SCALE_INCREASE' : 'LOG.SCALE_DECREASE',
            params: { amount },
            details: { resource: 'scale', amount: args.amount }
        };
    }

    formatSetConsciousness(args, sourceCard, gameState) {
        const player = gameState.players[args.player_id];
        if (!player) return null;
        return {
            messageKey: 'LOG.SET_CONSCIOUSNESS',
            params: { amount: player.consciousness },
            details: { newValue: player.consciousness }
        };
    }

    formatSetScale(args, sourceCard, gameState) {
        const player = gameState.players[args.player_id];
        if (!player) return null;
        return {
            messageKey: 'LOG.SET_SCALE',
            params: { amount: player.scale },
            details: { newValue: player.scale }
        };
    }

    formatActualConsciousnessChange(args, sourceCard, gameState) {
        const originalAmount = args.original_amount || 0;
        const actualAmount = args.actual_amount || 0;
        const originalStr = (originalAmount > 0 ? '+' : '') + originalAmount;

        let messageKey;
        // 基本のamountキーを常に含める（基本メッセージ再利用のため）
        const params = { 
            originalAmount: originalStr, 
            actualAmount: Math.abs(actualAmount),
            amount: Math.abs(actualAmount) 
        };

        if (originalAmount !== actualAmount) {
            if (actualAmount > 0) messageKey = 'LOG.CONSCIOUSNESS_INCREASE_CORRECTED';
            else if (actualAmount < 0) messageKey = 'LOG.CONSCIOUSNESS_DECREASE_CORRECTED';
            else messageKey = 'LOG.CONSCIOUSNESS_NULLIFIED';
        } else {
            if (actualAmount > 0) messageKey = 'LOG.CONSCIOUSNESS_INCREASE';
            else if (actualAmount < 0) messageKey = 'LOG.CONSCIOUSNESS_DECREASE';
            else messageKey = 'LOG.CONSCIOUSNESS_NO_EFFECT';
        }

        return { messageKey, params, details: { actualAmount, originalAmount } };
    }

    formatActualScaleChange(args, sourceCard, gameState) {
        const originalAmount = args.original_amount || 0;
        const actualAmount = args.actual_amount || 0;
        const originalStr = (originalAmount > 0 ? '+' : '') + originalAmount;

        let messageKey;
        // 基本のamountキーを常に含める
        const params = { 
            originalAmount: originalStr, 
            actualAmount: Math.abs(actualAmount),
            amount: Math.abs(actualAmount)
        };

        if (originalAmount !== actualAmount) {
            if (actualAmount > 0) messageKey = 'LOG.SCALE_INCREASE_CORRECTED';
            else if (actualAmount < 0) messageKey = 'LOG.SCALE_DECREASE_CORRECTED';
            else messageKey = 'LOG.SCALE_NULLIFIED';
        } else {
            if (actualAmount > 0) messageKey = 'LOG.SCALE_INCREASE';
            else if (actualAmount < 0) messageKey = 'LOG.SCALE_DECREASE';
            else messageKey = 'LOG.SCALE_NO_EFFECT';
        }

        return { messageKey, params, details: { actualAmount, originalAmount } };
    }

    formatActualCardDamage(args, sourceCard, gameState) {
        const targetCard = gameState.all_card_instances[args.card_id];
        if (!targetCard) return null;

        const originalAmount = args.original_amount || 0;
        const actualAmount = args.actual_amount || 0;
        const originalStr = (originalAmount > 0 ? '+' : '') + originalAmount;

        let messageKey;
        // 基本のamountキーを常に含める
        const params = { 
            targetCardId: targetCard.id,
            originalAmount: originalStr,
            actualAmount: Math.abs(actualAmount),
            amount: Math.abs(actualAmount)
        };

        if (originalAmount !== actualAmount) {
            if (actualAmount > 0) messageKey = 'LOG.WEALTH_RECOVER_CORRECTED';
            else if (actualAmount < 0) messageKey = 'LOG.WEALTH_DAMAGE_CORRECTED';
            else {
                params.originalTypeKey = originalAmount > 0 ? 'LOG.RECOVERY' : 'LOG.DAMAGE';
                messageKey = 'LOG.WEALTH_NULLIFIED';
            }
        } else {
            if (actualAmount > 0) messageKey = 'LOG.RECOVER_WEALTH';
            else if (actualAmount < 0) messageKey = 'LOG.DAMAGE_WEALTH';
            else messageKey = 'LOG.WEALTH_NO_EFFECT';
        }

        return { 
            messageKey, 
            params, 
            details: { targetCardId: targetCard.instance_id, actualAmount, originalAmount } 
        };
    }

    formatCardDraw(args, sourceCard, gameState) {
        return { messageKey: 'LOG.DRAW_ONE', params: {} };
    }

    formatExposeCard(args, sourceCard, gameState) {
        if (!args.exposed_cards || args.exposed_cards.length === 0) return null;
        return {
            messageKey: 'LOG.EXPOSE_CARD',
            params: {
                cardIds: args.exposed_cards.map(c => c.id)
            },
            details: {
                exposedCardIds: args.exposed_cards.map(c => c.instance_id)
            }
        };
    }

    formatRemoveCard(args, sourceCard, gameState) {
        const card = gameState.all_card_instances[args.card_id];
        if (!card) return null;
        return {
            messageKey: 'LOG.REMOVE_CARD',
            params: { cardId: card.id },
            details: { cardId: card.instance_id }
        };
    }

    formatRedevelopmentPlan(args, sourceCard, gameState) {
        const card = gameState.all_card_instances[args.card_id];
        if (!card) return null;
        return {
            messageKey: 'LOG.REDEVELOPMENT_PLAN',
            params: { cardId: card.id },
            details: { cardId: card.instance_id, playerId: args.player_id }
        };
    }

    formatTurnEnd(args, sourceCard, gameState) {
        return {
            messageKey: 'LOG.TURN_END',
            params: { playerType: args.player_id === 'PLAYER1' ? 'SELF' : 'OPPONENT' },
            details: { playerId: args.player_id }
        };
    }

    formatTurnStart(args, sourceCard, gameState) {
        return {
            messageKey: 'LOG.TURN_START',
            params: { playerType: args.player_id === 'PLAYER1' ? 'SELF' : 'OPPONENT' },
            details: { playerId: args.player_id }
        };
    }

    formatDefault(args, sourceCard, gameState) {
        return null;
    }

    /**
     * Adds a new formatter for a specific effect type.
     */
    addFormatter(effectType, formatter) {
        this.formatters[effectType] = formatter;
    }

    /**
     * Returns an array of all supported effect types.
     */
    getAvailableFormatters() {
        return Object.keys(this.formatters);
    }
}

export default LogEntryGenerator;
